/**
 * NAV_TREE — Declarative navigation tree definition.
 *
 * Single source of truth for all sidebar menu structure.
 * 선언 순서 = 사이드바 표시 순서.
 *
 * 4 도메인: 키워드, 콘텐츠, 검색광고, 인사이트
 */
import {
  Activity,
  AlertTriangle,
  BarChart3,
  BookOpen,
  BrainCircuit,
  CalendarDays,
  Database,
  FlaskConical,
  FileText,
  Gauge,
  Layers,
  LineChart,
  List,
  Megaphone,
  Network,
  PenTool,
  Radar,
  Search,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Globe,
  Wrench,
  Zap,
} from "lucide-react";
import { domain, group, leaf, tool, type DomainNode } from "./builders";

export const NAV_TREE: DomainNode[] = [
  /* ═══ 키워드 (검색키워드 + 검색결과 통합) ═══ */
  domain("키워드", [
    group(
      "키워드 분석",
      "/keywords/analysis",
      "어떤 키워드가 수익성이 높은지 검색량과 경쟁도를 분석합니다",
      Search,
      { category: "smart" },
      [
        leaf(
          "주제별 분류",
          "/keywords/analysis/clusters",
          "비슷한 의미의 키워드끼리 자동으로 묶어 콘텐츠 주제를 정리합니다",
          Network,
          {
            ai: true,
            assistantIntents: [
              "키워드",
              "주제",
              "클러스터",
              "검색",
              "방향",
              "아이디어",
            ],
            assistantPrompt: "키워드 방향과 주제를 먼저 정리하고 싶어",
          },
        ),
        leaf(
          "검색 의도 파악",
          "/keywords/analysis/intent",
          "사람들이 이 키워드를 왜 검색하는지(정보 탐색, 구매, 비교 등) 파악합니다",
          BrainCircuit,
          { ai: true },
        ),
        leaf(
          "경쟁사 키워드 비교",
          "/keywords/analysis/gap",
          "경쟁 채널은 노출되는데 우리는 빠진 키워드 기회를 찾아줍니다",
          Target,
          { ai: true },
        ),
      ],
    ),
    group(
      "키워드 데이터",
      "/keywords/data",
      "검색량, 순위 등 과거 데이터를 조회합니다",
      Database,
      { category: "data" },
      [
        leaf(
          "트렌드",
          "/keywords/data/trends",
          "키워드의 검색 관심도가 시간에 따라 어떻게 변하는지 추적합니다",
          TrendingUp,
        ),
        leaf(
          "월간 통계",
          "/keywords/data/stats",
          "PC와 모바일에서 매달 얼마나 검색되고 클릭되는지 비교합니다",
          BarChart3,
        ),
        leaf(
          "순위 변동 기록",
          "/keywords/data/rankings",
          "내 콘텐츠가 키워드 검색에서 몇 위에 노출되었는지 이력을 확인합니다",
          List,
        ),
      ],
    ),
    group(
      "검색결과",
      "/keywords/serp",
      "네이버 검색결과 페이지에서 어떤 채널이 얼마나 노출되는지 분석합니다",
      Layers,
      { category: "smart", ai: true },
      [
        leaf(
          "SERP 구성",
          "/keywords/serp/layout",
          "검색결과가 블로그·카페·뉴스 등 어떤 영역으로 구성되는지 시각화합니다",
          Layers,
          { ai: true },
        ),
        leaf(
          "상위 콘텐츠 벤치마크",
          "/keywords/serp/benchmark",
          "검색 상위 콘텐츠의 글자수, 이미지 수 등을 비교해 기준점을 파악합니다",
          Gauge,
          { ai: true },
        ),
        leaf(
          "연관검색어",
          "/keywords/serp/suggestions",
          "자동완성, 연관검색어, 추천검색어를 수집해 키워드 아이디어를 넓힙니다",
          Zap,
          { category: "data" },
        ),
      ],
    ),
    tool(
      "키워드 목록",
      "/keywords/explorer/list",
      "등록된 전체 키워드를 표 형태로 한눈에 조회하고 관리합니다",
      List,
      { category: "data" },
    ),
    tool(
      "검색결과 목록",
      "/keywords/serp/explorer",
      "블로그, 카페, 뉴스 등 채널별 검색결과 순위를 대량 조회합니다",
      Globe,
      { category: "data" },
    ),
    tool(
      "순위 추적",
      "/keywords/tracking",
      "키워드 검색 순위가 오르내리는 것을 자동으로 감시합니다",
      TrendingUp,
      { category: "automation" },
    ),
  ]),

  /* ═══ 채널 ═══ */
  domain("채널", [
    group(
      "채널 진단",
      "/channels/diagnostics",
      "콘텐츠 품질, 발행 빈도, 독자 반응을 종합 점검하고 개선 순서를 알려줍니다",
      Sparkles,
      { category: "smart", ai: true },
      [
        leaf(
          "건강 진단",
          "/channels/diagnostics/health",
          "채널의 종합 건강 점수를 매기고 5가지 핵심 지표를 상세히 진단합니다",
          Sparkles,
          { ai: true },
        ),
        leaf(
          "성장 예측",
          "/channels/diagnostics/forecast",
          "과거 데이터를 바탕으로 6개월 후 채널 성장을 낙관/비관으로 예측합니다",
          LineChart,
          { ai: true },
        ),
        leaf(
          "비교 분석",
          "/channels/diagnostics/compare",
          "내 채널과 경쟁 채널의 게시물 수, 방문자, 품질을 나란히 비교합니다",
          Radar,
          { ai: true },
        ),
        leaf(
          "채널 컨설팅",
          "/channels/diagnostics/consulting",
          "채널 URL을 입력하면 AI가 현재 상태를 진단하고 성장 전략을 제안합니다",
          Wrench,
          {
            ai: true,
            assistantIntents: ["채널", "진단", "성장", "전략", "유입", "성과"],
            assistantPrompt: "내 채널을 진단하고 성장 전략을 제안해줘",
          },
        ),
      ],
    ),
    tool(
      "채널 목록",
      "/channels/explorer",
      "블로거, 인플루언서 등 채널을 유형별로 대량 조회하고 창작자를 찾습니다",
      Users,
      { category: "data" },
    ),
    tool(
      "제휴 제안서",
      "/channels/proposals",
      "채널 성과와 독자층 분석을 바탕으로 브랜드 협업 제안서를 자동 생성합니다",
      FileText,
      {
        ai: true,
        assistantIntents: ["제안서", "제휴", "협업", "영업", "브랜드딜"],
        assistantPrompt: "협업용 제휴 제안서를 만들어줘",
      },
    ),
  ]),

  /* ═══ 콘텐츠 ═══ */
  domain("콘텐츠", [
    group(
      "글쓰기 & 최적화",
      "/contents/workspace",
      "AI 글쓰기, 검색 노출 점검, 발행 캘린더를 한 곳에서 관리합니다",
      PenTool,
      { category: "smart" },
      [
        leaf(
          "AI 글쓰기",
          "/contents/workspace/write-post",
          "키워드와 주제를 입력하면 검색 노출에 유리한 블로그 초안을 자동 작성합니다",
          Sparkles,
          {
            ai: true,
            assistantIntents: [
              "글쓰기",
              "초안",
              "블로그",
              "포스팅",
              "작성",
              "아티클",
            ],
            assistantPrompt: "블로그 초안을 바로 작성하고 싶어",
          },
        ),
        leaf(
          "글쓰기 스타일 분석",
          "/contents/workspace/brand-voice",
          "채널 URL을 입력하면 AI가 글쓰기 톤과 스타일을 분석하고 재현 가이드를 생성합니다",
          BrainCircuit,
          {
            ai: true,
            assistantIntents: [
              "톤",
              "스타일",
              "페르소나",
              "문체",
              "브랜드",
              "보이스",
            ],
            assistantPrompt: "내 채널 글쓰기 스타일을 분석해줘",
          },
        ),
        leaf(
          "시뮬레이터",
          "/contents/workspace/draft",
          "키워드를 입력하면 상위 노출에 필요한 글 구성을 미리 시뮬레이션합니다",
          PenTool,
        ),
        leaf(
          "검색 노출 점검",
          "/contents/workspace/optimize",
          "제목, 본문 길이, 이미지, 키워드 사용 등 검색 노출에 영향 주는 요소를 점검합니다",
          FlaskConical,
          {
            ai: true,
            assistantIntents: [
              "seo",
              "최적화",
              "본문",
              "제목",
              "이미지",
              "품질",
            ],
            assistantPrompt: "작성한 글의 검색 노출 상태를 점검해줘",
          },
        ),
        leaf(
          "발행 캘린더",
          "/contents/workspace/calendar",
          "계절성과 키워드 트렌드를 분석해 언제 어떤 주제로 글을 쓸지 추천합니다",
          CalendarDays,
          {
            category: "automation",
            ai: true,
            assistantIntents: [
              "캘린더",
              "일정",
              "발행",
              "계획",
              "월간",
              "주간",
            ],
            assistantPrompt: "다음 달 콘텐츠 발행 캘린더를 짜줘",
          },
        ),
      ],
    ),
    tool(
      "콘텐츠 목록",
      "/contents/explorer",
      "채널 유형별 콘텐츠 성과를 한눈에 조회합니다",
      Database,
      { category: "data" },
    ),
  ]),

  /* ═══ 검색광고 ═══ */
  domain("검색광고", [
    tool(
      "키워드 & 입찰",
      "/search-marketing/keywords/explorer",
      "키워드별 광고 입찰가, 검색량, 클릭률을 비교해 효율 좋은 키워드를 찾습니다",
      BarChart3,
      { category: "smart" },
    ),
    tool(
      "광고 문구 생성",
      "/search-marketing/workspace/copy-generator",
      "클릭을 유도하는 광고 제목과 설명문을 여러 버전으로 자동 생성합니다",
      BookOpen,
      { category: "smart", ai: true },
    ),
    group(
      "광고 최적화",
      "/search-marketing/optimization",
      "광고 수익률 시뮬레이션과 AI 입찰 전략으로 광고비 대비 효과를 높입니다",
      LineChart,
      { category: "smart", ai: true },
      [
        leaf(
          "수익률 시뮬레이터",
          "/search-marketing/optimization/roi-simulator",
          "광고 예산, 입찰가, 구매전환율을 조정하면 예상 수익을 바로 계산합니다",
          LineChart,
          { ai: true },
        ),
        leaf(
          "AI 입찰 최적화",
          "/search-marketing/optimization/bid-strategy",
          "시간대별 입찰 강도와 예산 배분을 AI가 자동으로 최적화합니다",
          Sparkles,
          { category: "automation", ai: true },
        ),
      ],
    ),
    tool(
      "캠페인 빌더",
      "/search-marketing/campaigns/builder",
      "상품 선택부터 입찰 설정까지 5단계로 검색광고 캠페인을 만듭니다",
      Megaphone,
      { category: "automation" },
    ),
  ]),

  /* ═══ 인사이트 (모니터링 축소) ═══ */
  domain("인사이트", [
    group(
      "이상 징후 감지",
      "/insights/anomalies",
      "순위 급락, 트래픽 급변 등 비정상 변화를 AI가 감지하고 보고합니다",
      Activity,
      { category: "automation", ai: true },
      [
        leaf(
          "실시간 감지",
          "/insights/anomalies/detection",
          "순위 급락, 트래픽 급변 등 비정상적인 패턴을 AI가 실시간 감지합니다",
          Sparkles,
          { ai: true },
        ),
        leaf(
          "경쟁 채널 감시",
          "/insights/anomalies/competitors",
          "경쟁 채널이 올린 새 콘텐츠, 순위 변동, 새로 진출한 키워드를 추적합니다",
          Radar,
          { ai: true },
        ),
      ],
    ),
    tool(
      "저품질 콘텐츠 찾기",
      "/insights/quality/low-quality",
      "글이 너무 짧거나, 이미지가 부족하거나, 내용이 중복된 콘텐츠를 자동으로 찾습니다",
      AlertTriangle,
      { category: "automation" },
    ),
  ]),
];

/* ─── Hidden routes: 라우트 등록은 필요하지만 사이드바에 표시 안 하는 경로 ─── */

export const HIDDEN_ROUTE_PATHS = [
  "/keywords/explorer/details/$id",
  "/keywords/tracking/positions",
  "/keywords/tracking/alerts",
  "/contents/analysis/summary",
  "/contents/analysis/trend-match",
  "/contents/workspace/draft/review",
] as const;
